import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from "@mui/material";

import { NavigateBefore } from "@mui/icons-material";
import { useFormik } from "formik";
import produce from "immer";
import { initialMarketCampaignChannel } from "../../../redux/marketCampaign/marketCampaign.types";
import {
  upsertMarketCampaignChannelAsync,
  upsertDraftStepAsync,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import { useBillingCompanyContext } from "../../../providers";

import { PageLoader } from "../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../constants/enums";
import { useMarketCampaignContext } from "../MarketCampaign.context-state";
import { useReduxStore } from "../../../redux/store.hooks";
import { TemplateDetailsV2 } from "../../../components/WhatsAppTemplateTableV2";
import {
  IUpsellInsight,
  IUpsellPayload,
} from "../../upselling/types/upsell.types";
import { api } from "../../../api/api";
import { endPoints } from "../../../constants/endPoints";
import { TabsList } from "../../../components/Tabs/TabsList";
import { TabPanel } from "../../../components/Tabs/TabPanel";
import {
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { RenderRecommendedTemplates } from "../components/RenderRecommendedTemplates";
import { ILead } from "../../../redux/leads/leads.types";

import { WhatsAppTemplateListV2 } from "../../WhatsAppTemplate/WhatsAppTemplateListV2";
import {
  IWhatsAppTemplate,
  IWhatsAppTemplateData,
} from "../../../redux/whatsappTemplate/whatsappTemplate.types";
import { INITIAL_WHATSAPP_TEMPLATE_DATA } from "../../../redux/whatsappTemplate/defaultState";
import { QuickReplyTemplateListV2 } from "../../QuickReplyTemplate/QuickReplyTemplateListV2";
import { ViewWhatsappDetails } from "../../leads/components/viewWhatsappDetails";

// 🔹 Utility: normalize meta template into consistent shape
const normalizeTemplate = (template: any): IWhatsAppTemplateData => {
  const components = template.components || [];
  return {
    ...template,
    header: components.find((c: any) => c.type === "HEADER") || {
      type: "HEADER",
      text: "",
      format: null,
    },
    body: components.find((c: any) => c.type === "BODY") || {
      type: "BODY",
      text: "",
    },
    footer: components.find((c: any) => c.type === "FOOTER") || {
      type: "FOOTER",
      text: "",
    },
    buttons: components.find((c: any) => c.type === "BUTTONS") || {
      type: "BUTTONS",
      buttons: [],
    },
  };
};

interface IStep3MarketCampaignChannelProps {
  leadRecommendedTemplates?: IWhatsAppTemplate[] | null;
  isFromUpsellLead?: boolean;
  isFromChatModule?: {
    toNumber?: string;
    isFromStartConversation?: boolean;
    isFromQuickReply?: boolean;
    onClose?: () => void;
  };
  createdRecommendTemplateData?: IWhatsAppTemplateData | null;
  setCreatedRecommendTemplateData?: (
    template: IWhatsAppTemplateData | null,
  ) => void;
  upsellData?: IUpsellInsight[] | null;
  leadData?: ILead | null;
  toNumber?: string;
}
export const Step3MarketCampaignChannel = React.memo(
  (props: IStep3MarketCampaignChannelProps) => {
    const {
      leadRecommendedTemplates,
      isFromUpsellLead,
      isFromChatModule = {
        toNumber: "",
        isFromStartConversation: false,
        isFromQuickReply: false,
      },

      createdRecommendTemplateData,
      setCreatedRecommendTemplateData,
      upsellData,
      leadData,
      toNumber,
    } = props;

    const dispatch = useDispatchWrapper();
    const companyOutlet = useBillingCompanyContext();

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { comapny } = useBillingCompanyContext() || {};
    const company_outlet_uuid = comapny?.company_outlet_uuid;

    const { state, isEditable, onStateChange } = useMarketCampaignContext();
    const { marketCampaign, draftSteps } = state;

    const { data: marketCampaignChannelData, dataLoading } =
      useReduxStore("marketCampaign").marketCampaignChannel;

    const [showTemplateDetails, setShowTemplateDetails] =
      useState<IWhatsAppTemplateData | null>(null);

    const [closeBtnClicked, setCloseBtnClicked] = useState(false);
    const [headerMedia, setHeaderMedia] = useState<File | null>(null);
    const [headerType, setHeaderType] = useState<string>("");

    const [showCardsView, setShowCardsView] = useState(true);
    const [tab, setTab] = React.useState(1);
    const [templateLoading, setTemplateLoading] =
      React.useState<boolean>(false);
    const [recommendedTemplate, setRecommendedTemplate] = React.useState<
      IWhatsAppTemplate[] | null
    >(null);

    const isVenueUpsellCategoryName =
      draftSteps?.draft_step_data?.step_two_data?.saveQuickSendCampaignData
        ?.quick_send_list[0]?.upsell_category_name === "VENUE";

    const isAutoGeneratedCampaign = Boolean(
      draftSteps?.draft_step_data?.step_two_data?.saveQuickSendCampaignData
        ?.quick_send_list[0]?.upsell_category_name,
    );

    // 🔹 Formik setup
    const { values, setValues } = useFormik({
      initialValues: {
        ...marketCampaignChannelData,
        market_campaign_channel_name: "WHATSAPP",
        company_outlet_uuid,
        market_campaign_uuid: marketCampaign.market_campaign_uuid || "",
      },
      onSubmit: (values) => {
        const payload = {
          ...values,
          module_variables_columns_name:
            values.module_variables_columns_name && values.module_variables_columns_name.length > 0
              ? values.module_variables_columns_name.map((v: any) => ({
                type: v.type || "",
                value: v.value || "",
              }))
              : [],
          button_variable_column_name: values.button_variable_column_name || [],
          company_outlet_uuid,
          header_media: values.header_media
            ? values.header_media.map((item) => ({
              ...item,
              file_type: headerType,
            }))
            : null,

          components: showTemplateDetails
            ? [
              showTemplateDetails.header,
              showTemplateDetails.body,
              showTemplateDetails.footer,
              showTemplateDetails.buttons,
            ].filter(Boolean)
            : [],
        };

        dispatch(
          upsertMarketCampaignChannelAsync(
            payload,
            values?.header_media?.[0]?.type === "UPLOAD" ? headerMedia : null,
            true,
            () => {
              dispatch(
                upsertDraftStepAsync(
                  {
                    draft_step_uuid: marketCampaign.market_campaign_uuid || "",
                    total_steps: 4,
                    current_step: 3,
                    draft_step_data: {
                      // Old prevWhatsAppTemplate usage (commented out)
                      // template_data: prevWhatsAppTemplate,
                      // New: Using showTemplateDetails
                      template_data: showTemplateDetails,
                      step_three_data: payload,
                    },
                    status: "ACTIVE",
                  },
                  handelNextButtonClick,
                ),
              );
            },
          ),
        );
      },
    });

    // Template list, pagination, search, and handleTemplateSelect now in WhatsAppTemplateListV2

    const handelNextButtonClick = () =>
      onStateChange({ ...state, activeStep: 3, maxAllowedSteps: 3 });

    const handelBackToStep2ButtonClick = () =>
      onStateChange({
        ...state,
        activeStep: 1,
        maxAllowedSteps: draftSteps?.current_step,
      });

    const handleRecommendedTemplatesClick = async (payload: IUpsellPayload) => {
      try {
        setTemplateLoading(true);

        const autoGeneratedCampaignPayload = {
          company_outlet_uuid: comapny.company_outlet_uuid,
          // service_templates: payload.service_templates,
          // venue_templates: payload.venue_templates,
          leads_uuid: leadData?.leads_uuid || "",
        };

        const manualCampaignPayload = {
          company_outlet_uuid: comapny.company_outlet_uuid,
          service_templates: 0,
          venue_templates: 0,
          others: {
            others:
              draftSteps?.draft_step_data?.step_two_data
                ?.saveQuickSendCampaignData?.quick_send_list[0],
          },
        };

        const res = await api.get(`${endPoints.upsell.upsellTemplate}`, {
          // params: isAutoGeneratedCampaign
          params: isFromUpsellLead
            ? autoGeneratedCampaignPayload
            : manualCampaignPayload,
        });

        setRecommendedTemplate(res.data.data);
        // setTab(0);
        setTemplateLoading(false);
      } catch (err: any) {
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        });
        setTemplateLoading(false);
      } finally {
        setTemplateLoading(false);
      }
    };

    const handleTabChange = (event: React.SyntheticEvent, value: number) => {
      setTab(value);
    };

    // Template list, pagination, search, and selection now handled by WhatsAppTemplateListV2 component

    React.useEffect(() => {
      const selectedTemplate =
        draftSteps?.draft_step_data?.step_three_data?.selected_template;

      const saved_template_data =
        draftSteps?.draft_step_data?.step_three_data?.saved_template_data;

      if (selectedTemplate) {
        setShowTemplateDetails(selectedTemplate);
        const newValues = produce(values, (draft) => {
          draft.template_name = selectedTemplate.name;
          draft.company_outlet_uuid = company_outlet_uuid;
          draft.template_uuid = selectedTemplate.name as any;
          draft.market_campaign_channel_uuid = marketCampaign.market_campaign_uuid;
          draft.module_variables_columns_name = [];
          setHeaderMedia(null);

          if (saved_template_data) {
            setHeaderType(saved_template_data?.header_media?.[0]?.type);
            draft.header_media = saved_template_data?.header_media;
          } else {
            setHeaderType("");
            draft.header_media = [];
          }
        });
        // Old prevWhatsAppTemplate usage (commented out)
        // setPrevWhatsAppTemplate(normalizeTemplate(selectedTemplate));
        setShowTemplateDetails(normalizeTemplate(selectedTemplate));
        setValues(newValues);
      }
    }, [draftSteps]);

    // Template fetching now handled by WhatsAppTemplateListV2 component

    React.useEffect(() => {
      const saved_template_data =
        draftSteps?.draft_step_data?.step_three_data?.saved_template_data;

      if (
        !isFromUpsellLead &&
        !recommendedTemplate &&
        !saved_template_data &&
        !isFromChatModule
      ) {
        handleRecommendedTemplatesClick({
          company_outlet_uuid: company_outlet_uuid,
          service_templates: isVenueUpsellCategoryName ? 0 : 1,
          venue_templates: isVenueUpsellCategoryName ? 1 : 0,
        });
      }
    }, [
      isFromUpsellLead,
      isFromChatModule,
      isVenueUpsellCategoryName,
      recommendedTemplate,
      draftSteps?.draft_step_data?.step_three_data?.saved_template_data,
    ]);

    React.useEffect(() => {
      if (isFromUpsellLead) {
        setTab(0);
      }
    }, [isFromUpsellLead]);


    console.log("showTemplateDetails ==> ", {
      showTemplateDetails,
      isFromUpsellLead,
      isFromChatModule,
      createdRecommendTemplateData,
    })

    const isFormLocked = draftSteps?.draft_step_data?.step_three_data?.lock == 1
    return (
      <>
        <PageLoader loading={dataLoading === LoadState.InProgress}>
          <Grid container spacing={5} justifyContent="center">
            {!showTemplateDetails && !createdRecommendTemplateData && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: "100%",
                    p: isMobile && isFromChatModule ? 1 : 2,
                  }}
                >
                  {/* {!isFromChatModule && ( */}
                  <TabsList
                    sx={{
                      mb: 2,
                      ...(isMobile && isFromChatModule
                        ? {
                          padding: "4px",
                          "& .MuiTab-root": {
                            padding: "6px 8px",
                            fontSize: "0.75rem",
                            minHeight: "auto",
                            "& .MuiTab-iconWrapper": {
                              marginRight: "4px",
                              "& svg": {
                                fontSize: "0.875rem",
                              },
                            },
                          },
                        }
                        : {}),
                    }}
                    varaint="fullWidth"
                    selectedTab={tab}
                    tabs={[
                      {
                        label: `Recommended Templates ${recommendedTemplate && recommendedTemplate.length > 0
                          ? `(${recommendedTemplate.length})`
                          : leadRecommendedTemplates &&
                            leadRecommendedTemplates.length > 0
                            ? `(${leadRecommendedTemplates.length})`
                            : ""
                          }`,
                        icon: <AutoAwesomeIcon />,
                        iconPosition: "start",
                      },
                      {
                        label: "Existing Templates",
                        icon: <ContentCopyIcon />,
                        iconPosition: "start",
                      },
                    ]}
                    onChange={(event, value) => handleTabChange(event, value)}
                  />
                  {/* )} */}

                  {/* Recommended Templates Tab */}
                  <TabPanel value={tab} index={0}>
                    <Box sx={{ py: 2 }}>
                      <RenderRecommendedTemplates
                        recommendedTemplate={
                          leadRecommendedTemplates
                            ? leadRecommendedTemplates
                            : recommendedTemplate || null
                        }
                        onTemplateCreateSuccess={(templateData) => {
                          leadRecommendedTemplates
                            ? setCreatedRecommendTemplateData?.(templateData)
                            : setShowTemplateDetails(templateData);
                        }}
                        onHandleRecommendedTemplatesClick={() =>
                          handleRecommendedTemplatesClick({
                            company_outlet_uuid: company_outlet_uuid,
                            service_templates: isVenueUpsellCategoryName
                              ? 0
                              : 1,
                            venue_templates: isVenueUpsellCategoryName ? 1 : 0,
                          })
                        }
                        showCardsView={showCardsView}
                        onToggleCardsView={setShowCardsView}
                        templateLoading={templateLoading}
                        leadData={leadData || null}
                        toNumber={toNumber || null}
                      />
                    </Box>
                  </TabPanel>

                  {/* Existing Templates Tab */}
                  <TabPanel value={tab} index={1}>
                    <WhatsAppTemplateListV2
                      onTemplateSelect={(name, companyOutletUuid, template) => {
                        // Update formik values when checkbox is clicked
                        const newValues = produce(values, (draft) => {
                          draft.template_name = template.name;
                          draft.company_outlet_uuid = companyOutletUuid;
                          draft.template_uuid = template.name as any;
                          draft.market_campaign_channel_uuid =
                            marketCampaign.market_campaign_uuid;
                          draft.module_variables_columns_name = [];
                          setHeaderMedia(null);

                          if (template.header?.format) {
                            setHeaderType(template.header.format);
                            draft.header_media = [
                              {
                                type:
                                  template.header.format === "IMAGE" ||
                                    template.header.format === "VIDEO" ||
                                    template.header.format === "DOCUMENT"
                                    ? "UPLOAD"
                                    : "SELECT",
                                value: "",
                              },
                            ];
                          } else {
                            setHeaderType("");
                            draft.header_media = [];
                          }
                        });
                        setValues(newValues);
                      }}
                      onTemplateDetails={(template) => {
                        // Show template details view when card/row is clicked
                        setShowTemplateDetails(normalizeTemplate(template));
                      }}
                      selectedTemplateUuid={
                        values?.market_campaign_channel_uuid
                      }
                      showCreateButton={true}
                      showSelectionColumn={true}
                      isFromCampaign={true}
                      isFromChatModule={isFromChatModule}
                    />
                  </TabPanel>
                </Box>
              </Grid>
            )}

            {showTemplateDetails &&
              !isFromUpsellLead &&
              !isFromChatModule?.toNumber && (
                <Grid item xs={12}>
                  <TemplateDetailsV2
                    onNext={handelNextButtonClick}
                    values={values}
                    template={showTemplateDetails}
                    closeBtnClicked={closeBtnClicked}
                    isLocked={isFormLocked}
                    onClose={() => {
                      setCloseBtnClicked(true);
                      setShowTemplateDetails(null);
                      isFromChatModule?.onClose?.();
                    }}
                  />
                </Grid>
              )}

            {isFromChatModule?.toNumber &&
              showTemplateDetails &&
              !isFromUpsellLead && (
                <Grid item xs={12}>
                  <TemplateDetailsV2
                    onNext={() => { }}
                    values={values}
                    template={showTemplateDetails}
                    closeBtnClicked={closeBtnClicked}
                     isLocked={isFormLocked}
                    onClose={() => {
                      setCloseBtnClicked(true);
                      setShowTemplateDetails(null);
                    }}
                    onDialogClose={isFromChatModule?.onClose}
                    showButtons={false}
                    toNumber={isFromChatModule?.toNumber}
                  />
                </Grid>
              )}

            {(createdRecommendTemplateData || showTemplateDetails) &&
              isFromUpsellLead &&
              !isFromChatModule?.toNumber && (
                <Grid item xs={12}>
                  <TemplateDetailsV2
                    onNext={() => { }}
                    values={initialMarketCampaignChannel}
                    template={
                      createdRecommendTemplateData
                        ? createdRecommendTemplateData
                        : showTemplateDetails || INITIAL_WHATSAPP_TEMPLATE_DATA
                    }
                    onClose={() => {
                      setCreatedRecommendTemplateData?.(null);
                    }}
                    showButtons={false}
                    upsellData={upsellData}
                    leadData={leadData}
                    toNumber={toNumber}
                  />
                </Grid>
              )}

            {isEditable && (
              <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                <Stack
                  direction={isDesktop ? "row" : "column"}
                  spacing={2}
                  mr={4}
                  gap={2}
                >
                  {(showTemplateDetails || createdRecommendTemplateData) && (
                    <Button
                      startIcon={<NavigateBefore />}
                      variant="contained"
                      onClick={() => {
                        setCloseBtnClicked(true);
                        setShowTemplateDetails(null);
                        setCreatedRecommendTemplateData?.(null);
                      }}
                    >
                      Back to Templates
                    </Button>
                  )}

                  {!isFromUpsellLead && !isFromChatModule && (
                    <Button
                      variant="contained"
                      startIcon={<NavigateBefore />}
                      onClick={handelBackToStep2ButtonClick}
                    >
                      Back to Step 2
                    </Button>
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </PageLoader>
      </>
    );
  },
);
